/**
 * Subscription Paywall Component
 * Beautiful upgrade modal with pricing and feature comparison
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Check,
  Sparkles,
  Zap,
  Crown,
  ArrowRight,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SUBSCRIPTION_TIERS, TIER_CONFIG } from '../../config/subscriptionTiers';
import { useStatus } from '../../contexts/StatusContext';
import { Make_Authorized_API_Call } from '../../api/apiClient';

// Map frontend tier names to backend tier IDs
const TIER_TO_BACKEND_ID = {
  'Starter': 'starter',
  'Premium': 'family',
  'Enterprise': 'property_manager'
};

const SubscriptionPaywall = ({ isOpen, onClose, onUpgrade, currentTier, upgradeMessage }) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'
  const [isProcessing, setIsProcessing] = useState(false);
  const { user_id, family_id } = useStatus();

  const handleUpgradeClick = async (tier) => {
    if (tier === currentTier) {
      toast.success('You\'re already on this plan!');
      return;
    }

    if (tier === SUBSCRIPTION_TIERS.STARTER) {
      toast.error('You cannot downgrade to Free from this modal');
      return;
    }

    const backendTierId = TIER_TO_BACKEND_ID[tier];
    if (!backendTierId) {
      toast.error('Invalid tier selected');
      return;
    }

    if (!user_id || !family_id) {
      toast.error('Please complete your profile setup first');
      return;
    }

    setIsProcessing(true);

    try {
      const response = await Make_Authorized_API_Call('/subscription', 'POST', {
        tier: backendTierId,
        billingPeriod: billingPeriod === 'yearly' ? 'annual' : 'monthly',
        familyId: family_id,
        userId: user_id
      });

      if (response?.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Stripe checkout error:', err);
      const message = err?.data?.message || err?.message || 'Failed to start checkout';
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const tiers = [
    TIER_CONFIG[SUBSCRIPTION_TIERS.STARTER],
    TIER_CONFIG[SUBSCRIPTION_TIERS.PREMIUM],
    TIER_CONFIG[SUBSCRIPTION_TIERS.ENTERPRISE]
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-card w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Unlock More Collaboration
              </h2>
              {upgradeMessage && (
                <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg mb-4">
                  <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">
                    {upgradeMessage.title}
                  </h3>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {upgradeMessage.description}
                  </p>
                </div>
              )}
              <p className="text-gray-600 dark:text-gray-400">
                Choose the plan that fits your collaboration needs
              </p>
            </div>
            <button
              onClick={onClose}
              className="btn-icon"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Billing Period Toggle */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${billingPeriod === 'monthly'
                  ? 'bg-gradient-honey text-white shadow-glow'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }
              `}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all relative
                ${billingPeriod === 'yearly'
                  ? 'bg-gradient-honey text-white shadow-glow'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }
              `}
            >
              Yearly
              <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                Save 17%
              </span>
            </button>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {tiers.map((tier, index) => {
              const isCurrentTier = tier.name === currentTier;
              const isPremium = tier.popular;

              return (
                <motion.div
                  key={tier.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`
                    relative rounded-2xl p-6 border-2 transition-all
                    ${isPremium
                      ? 'border-honey-500 shadow-glow bg-gradient-to-br from-honey-50 to-orange-50 dark:from-honey-900/20 dark:to-orange-900/20'
                      : 'border-gray-200 dark:border-gray-700 glass-card'
                    }
                    ${isCurrentTier ? 'ring-2 ring-green-500 ring-offset-2' : ''}
                  `}
                >
                  {/* Popular Badge */}
                  {isPremium && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="flex items-center gap-1 px-4 py-1 bg-gradient-honey text-white rounded-full text-xs font-bold shadow-lg">
                        <Sparkles className="w-3 h-3" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {isCurrentTier && (
                    <div className="absolute -top-3 right-4">
                      <div className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-bold shadow-lg">
                        Current Plan
                      </div>
                    </div>
                  )}

                  {/* Tier Icon */}
                  <div className="flex items-center justify-center mb-4">
                    {tier.name === 'Starter' && (
                      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <Zap className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                      </div>
                    )}
                    {tier.name === 'Premium' && (
                      <div className="w-12 h-12 bg-gradient-honey rounded-full flex items-center justify-center shadow-glow">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                    )}
                    {tier.name === 'Enterprise' && (
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Tier Name */}
                  <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
                    {tier.name}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-center text-gray-600 dark:text-gray-400 mb-4">
                    {tier.description}
                  </p>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-gray-900 dark:text-white">
                      ${billingPeriod === 'monthly' ? tier.price : Math.floor(tier.price * 12 * 0.83)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      per {billingPeriod === 'monthly' ? 'month' : 'year'}
                    </div>
                    {billingPeriod === 'yearly' && tier.yearlyDiscount && (
                      <div className="text-xs text-green-600 dark:text-green-400 font-medium mt-1">
                        {tier.yearlyDiscount}
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {tier.featureList.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleUpgradeClick(tier.name)}
                    disabled={isCurrentTier || isProcessing}
                    className={`
                      w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2
                      ${isCurrentTier
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : isPremium
                        ? 'bg-gradient-honey text-white shadow-glow hover:shadow-xl'
                        : 'bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-800 dark:hover:bg-gray-600'
                      }
                      ${isProcessing ? 'opacity-75 cursor-wait' : ''}
                    `}
                  >
                    {isCurrentTier ? 'Current Plan' : isProcessing ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                    ) : (
                      <>{tier.cta} <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Security Notice */}
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
            <p className="text-sm text-green-700 dark:text-green-400">
              Secure payment processing powered by Stripe. Cancel anytime.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SubscriptionPaywall;
